import { Search, PenLine, Megaphone, Heart } from 'lucide-react';

export const DEFAULT_MODEL_ID = 'openai/gpt-6-luna';

export const MODEL_OPTIONS = [
  { id: 'openai/gpt-6-luna', label: 'GPT-6 Luna' },
  { id: 'openai/gpt-6-sol', label: 'GPT-6 Sol' },
  { id: 'openai/gpt-6-astra', label: 'GPT-6 Astra' },
  { id: 'openai/gpt-5-nano', label: 'GPT-5 Nano' },
  { id: 'anthropic/claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'deepseek/deepseek-v4.1-flash', label: 'DeepSeek v4.1 Flash' },
];

export const SUGGESTION_GROUPS = [
  {
    label: 'Give me feedback',
    labelKey: 'suggestions.group-give-me-feedback',
    icon: Search,
    iconColor: 'text-blue-600',
    options: [
      {
        title: 'What is my most valuable art piece?',
        titleKey: 'suggestions.most-valuable-piece',
        prompt:
          'What is my most valuable art piece? Check my already uploaded artwork, and also display the uploader so I can upload as many images as I want.',
      },
      {
        title: 'Review/Critique my Art',
        titleKey: 'suggestions.review-critique',
        prompt: 'Please review my art and provide feedback on style, technique, and market potential.',
      },
      {
        title: 'How sellable is this?',
        titleKey: 'suggestions.how-sellable',
        prompt: 'How sellable is my art? What market potential does it have and what price range should I consider?',
      },
      {
        title: 'What should I do next?',
        titleKey: 'suggestions.what-next',
        prompt: 'What should I do next with my art? Give me actionable steps to improve and grow as an artist.',
      },
      {
        title: 'What other subject matter should I create?',
        titleKey: 'suggestions.other-subject-matter',
        prompt:
          'What other subject matter should I create? Suggest new themes, topics, or styles I should explore based on my current work.',
      },
    ],
  },
  {
    label: 'Write Content',
    labelKey: 'suggestions.group-write-content',
    icon: PenLine,
    iconColor: 'text-green-600',
    options: [
      {
        title: 'Write a title',
        titleKey: 'suggestions.write-title',
        prompt:
          'Help me write compelling titles for my artwork. Create several options that are catchy, descriptive, and would appeal to potential buyers.',
      },
      {
        title: 'Write a product description',
        titleKey: 'suggestions.write-product-description',
        prompt:
          'Write a professional product description for my artwork that highlights its features, style, and appeal to help with sales.',
      },
      {
        title: 'Write a social media post',
        titleKey: 'suggestions.write-social-media-post',
        prompt:
          'Create an engaging social media post about my artwork that would get attention, engagement, and potentially drive sales.',
      },
      {
        title: 'Write a blog post',
        titleKey: 'suggestions.write-blog-post',
        prompt:
          'Help me write a blog post about my art, artistic process, or journey as an artist that would engage readers and showcase my work.',
      },
    ],
  },
  {
    label: 'Promote my art',
    labelKey: 'suggestions.group-promote-my-art',
    icon: Megaphone,
    iconColor: 'text-purple-600',
    options: [
      {
        title: 'Write an Instagram post',
        titleKey: 'suggestions.write-instagram-post',
        prompt:
          'Create an Instagram post for my artwork with engaging caption, relevant hashtags, and strategy to increase visibility and engagement.',
      },
      {
        title: 'Write a Facebook post',
        titleKey: 'suggestions.write-facebook-post',
        prompt:
          'Help me write a Facebook post about my art that would engage my audience and potentially drive sales or commissions.',
      },
      {
        title: 'Write a LinkedIn post',
        titleKey: 'suggestions.write-linkedin-post',
        prompt:
          'Create a professional LinkedIn post about my artwork or artistic journey that would appeal to a business audience and potential collectors.',
      },
      {
        title: 'Grow my audience/followers',
        titleKey: 'suggestions.grow-audience',
        prompt:
          'Give me strategies and actionable tips to grow my social media following and build an engaged audience for my art.',
      },
      {
        title: 'Find interior designers',
        titleKey: 'suggestions.find-interior-designers',
        prompt:
          'Help me find and connect with interior designers who might be interested in featuring my artwork in their projects.',
      },
      {
        title: 'Find local art shows',
        titleKey: 'suggestions.find-local-art-shows',
        prompt:
          'Help me find local art shows, exhibitions, and gallery opportunities where I can display and sell my artwork.',
      },
      {
        title: 'Find art galleries',
        titleKey: 'suggestions.find-art-galleries',
        prompt:
          'Guide me on how to find and approach art galleries that might be interested in representing or showcasing my work.',
      },
    ],
  },
  {
    label: 'Lift my mood',
    labelKey: 'suggestions.group-lift-my-mood',
    icon: Heart,
    iconColor: 'text-pink-600',
    options: [
      {
        title: "I'm feeling down. Lift me up!",
        titleKey: 'suggestions.lift-me-up',
        prompt:
          "I'm feeling down about my art and creative journey. Please lift my spirits with encouragement, positive affirmations, and remind me why art matters.",
      },
      {
        title: "I'm uninspired. Inspire me!",
        titleKey: 'suggestions.inspire-me',
        prompt:
          "I'm feeling uninspired and stuck in my creative process. Please inspire me with new ideas, techniques, and motivation to get back to creating.",
      },
      {
        title: "I'm demotivated. Motivate me!",
        titleKey: 'suggestions.motivate-me',
        prompt:
          "I'm feeling demotivated about my art career and progress. Please motivate me with encouragement and practical steps to keep moving forward.",
      },
    ],
  },
];

export const ALL_SUGGESTION_TITLES = SUGGESTION_GROUPS.flatMap((group) => group.options.map((option) => option.title));

// Delay before submitting pending message to ensure UI state is ready
export const PENDING_MESSAGE_SUBMIT_DELAY = 100;
